import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  CardContent,
  CardMedia,
  Button,
  Rating,
  Stack,
  Chip,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
// import { apiList, invokeApi } from "../../services/apiServices";
// import { config } from "../../config/config";
import { useNavigate } from "react-router-dom";

const RecentlyPurchased = ({ onCartUpdate, filterCategory }) => {
  const [productsData, setProductsData] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [openCheckout, setOpenCheckout] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const scrollRef = useRef(null);
  const navigate = useNavigate();

  // Animation variants to match Courses component
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
  };

  // Load cart from session storage
  useEffect(() => {
    const storedCart = sessionStorage.getItem("cart");
    if (storedCart) {
      setCartItems(JSON.parse(storedCart));
    }
  }, [onCartUpdate]);

  // Auto-scroll effect
  useEffect(() => {
    const scrollInterval = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        if (scrollLeft + clientWidth >= scrollWidth - 1) {
          scrollRef.current.scrollTo({ left: 0, behavior: "instant" });
        } else {
          scrollRef.current.scrollBy({ left: 10, behavior: "smooth" });
        }
      }
    }, 100);

    return () => clearInterval(scrollInterval);
  }, []);

  // Fetch products
  const fetchProductData = async () => {
    // try {
    //   const response = await invokeApi(
    //     config.getMyCollege + apiList.getAllProducts,
    //     {}
    //   );
    //   if (response?.status === 200) {
    //     setProductsData(response.data.products);
    //   } else {
    //     console.error("Failed to get products!");
    //   }
    // } catch (error) {
    //   console.error("Error fetching products:", error);
    // }
  };

  useEffect(() => {
    fetchProductData();
  }, []);

  // Add to cart (with counter)
  const handleAddToCart = (product) => {
    const existingItem = cartItems.find((item) => item.id === product.id);
    let updatedCart;

    if (existingItem) {
      updatedCart = cartItems.map((item) =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      updatedCart = [...cartItems, { ...product, quantity: 1 }];
    }

    setCartItems(updatedCart);
    sessionStorage.setItem("cart", JSON.stringify(updatedCart));
    if (onCartUpdate) onCartUpdate(updatedCart);
  };

  // Remove from cart
  const handleRemoveFromCart = (productId) => {
    const existingItem = cartItems.find((item) => item.id === productId);
    let updatedCart;

    if (existingItem.quantity > 1) {
      updatedCart = cartItems.map((item) =>
        item.id === productId
          ? { ...item, quantity: item.quantity - 1 }
          : item
      );
    } else {
      updatedCart = cartItems.filter((item) => item.id !== productId);
    }

    setCartItems(updatedCart);
    sessionStorage.setItem("cart", JSON.stringify(updatedCart));
    if (onCartUpdate) onCartUpdate(updatedCart);
  };

  // Buy Now (single item checkout)
  const handleBuyProduct = (product) => {
    const updatedCart = [{ ...product, quantity: 1 }];
    sessionStorage.setItem("cart", JSON.stringify(updatedCart));
    setCartItems(updatedCart);
    if (onCartUpdate) onCartUpdate(updatedCart);
    setOpenCheckout(true);
  };

  // Calculate totals
  const calculateTotals = () => {
    const items = Array.isArray(cartItems) ? cartItems : [];
    const subtotal = items.reduce(
      (sum, item) => sum + (item.price || 0) * (item.quantity || 0),
      0
    );
    const discount = items.reduce(
      (sum, item) =>
        sum +
        ((item.scratchPrice || 0) - (item.price || 0)) *
          (item.quantity || 0),
      0
    );
    return {
      subtotal,
      discount,
      total: subtotal,
      savings: discount,
    };
  };

  const { total, savings } = calculateTotals();

  const filteredProducts =
    filterCategory === "All"
      ? productsData
      : productsData.filter((p) => p.category === filterCategory);

  return (
    <>
      <Box
        component={motion.div}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.2 }}
        sx={{
          p: 4,
          // maxWidth: 1200,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          textAlign: "center",
          py: 1,
          bgcolor: "rgba(255, 234, 223, 0.3)",
        }}
      >
        {/* Heading */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              py: 3.5,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: "30px",
                fontFamily: "inherit",
                color: "#6d1675",
                letterSpacing: "0.5px",
                mb: 0,
              }}
            >
              Top Rated & <span style={{ color: "#ffc20f" }}>Most Bought</span>
            </Typography>

            {/* Flower Divider */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                maxWidth: 300,
                mx: "auto",
                mt: 1,
              }}
            >
              <Box sx={{ flex: 1, height: 3, bgcolor: "#6d1675" }} />
              <Box
                sx={{
                  px: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LocalFloristIcon sx={{ fontSize: 28, color: "#ffc20f" }} />
              </Box>
              <Box sx={{ flex: 1, height: 3, bgcolor: "#6d1675" }} />
            </Box>
          </Box>
        </motion.div>

        {/* Products */}
        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "nowrap",
            overflowX: "auto",
            scrollbarWidth: "none",
            gap: 4,
            pb: 2,
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {filteredProducts.map((item, idx) => {
            const cartItem = cartItems.find((ci) => ci.id === item.id);
            const quantity = cartItem ? cartItem.quantity : 0;

            return (
              <motion.div
                key={idx}
                variants={cardVariants}
                whileHover={{ 
                  scale: 1.03,
                  transition: { duration: 0.3 }
                }}
              >
                <Box
                  sx={{
                    borderRadius: "16px",
                    position: "relative",
                    overflow: "visible",
                    border: "2px solid #f0f0f0",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    minWidth: 280,
                    backgroundColor: "#fff",
                    boxShadow: "0 4px 15px rgba(109, 22, 117, 0.1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 8px 25px rgba(109, 22, 117, 0.2)",
                      borderColor: "#ffc20f",
                    },
                  }}
                  onClick={() => navigate(`/products/${item.ogUrl}`)}
                >
                  {/* Labels */}
                  <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 2 }}>
                    <Chip
                      label="New Launch"
                      sx={{
                        backgroundColor: "#6d1675",
                        color: "white",
                        fontWeight: "bold",
                        borderRadius: "8px",
                        fontSize: 11,
                        height: 24,
                      }}
                    />
                  </Box>
                  <Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}>
                    <Chip
                      label={`${Math.round(
                        ((item.scratchPrice - item.price) * 100) /
                          item.scratchPrice
                      )}% Off`}
                      sx={{
                        backgroundColor: "#ffc20f",
                        color: "#6d1675",
                        fontWeight: "bold",
                        borderRadius: "8px",
                        fontSize: 11,
                        height: 24,
                      }}
                    />
                  </Box>

                  {/* Image */}
                  <CardMedia
                    component="img"
                    image={item.featuredImage}
                    alt={item.productName}
                    sx={{
                      objectFit: "contain",
                      height: 220,
                      p: 2,
                      backgroundColor: "#fcf6eb",
                      borderRadius: "14px 14px 0 0",
                    }}
                  />

                  {/* Content */}
                  <CardContent sx={{ px: 2, py: 1.5 }}>
                    <Typography 
                      color="text.secondary" 
                      sx={{ 
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#666"
                      }}
                    >
                      {item.category}
                    </Typography>
                    <Typography 
                      fontWeight="bold" 
                      sx={{ 
                        my: 0.4, 
                        fontSize: "16px",
                        color: "#6d1675",
                        minHeight: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      {item.productName}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{ mb: 0.8, fontSize: "14px", color: "#888" }}
                    >
                      {item.subCategory}
                    </Typography>

                    {/* Rating + Price */}
                    <Box sx={{ textAlign: "center" }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 1, justifyContent: "center" }}
                      >
                        <Rating 
                          value={4.5} 
                          precision={0.5} 
                          readOnly 
                          size="small" 
                          sx={{
                            "& .MuiRating-iconFilled": {
                              color: "#ffc20f",
                            }
                          }}
                        />
                        <Typography variant="body2" sx={{ color: "#666", fontSize: "12px" }}>
                          1088 reviews
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography 
                          variant="h6" 
                          fontWeight="bold"
                          sx={{ color: "#6d1675" }}
                        >
                          ₹{item.price}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            textDecoration: "line-through",
                            color: "#999",
                            fontSize: "14px",
                          }}
                        >
                          ₹{item.scratchPrice}
                        </Typography>
                      </Stack>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "#ffc20f",
                        fontWeight: "bold",
                        fontSize: "12px",
                        mt: 0.5,
                      }}
                    >
                      Best Price ₹{item.price}{" "}
                      <Typography component="span" sx={{ color: "#666" }}>
                        with coupon
                      </Typography>
                    </Typography>
                  </CardContent>

                  {/* Actions */}
                  <Stack
                    spacing={1.5}
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ width: "100%", mb: 2, px: 2 }}
                  >
                    {quantity > 0 ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 50,
                          overflow: "hidden",
                          px: 1,
                          borderColor: "#6d1675",
                          backgroundColor: "#f8f4ff",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFromCart(item.id);
                          }}
                          sx={{ color: "#6d1675" }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ px: 1, fontWeight: "bold", color: "#6d1675" }}>
                          {quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToCart(item);
                          }}
                          sx={{ color: "#6d1675" }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    ) : (
                      <Button
                        variant="contained"
                        startIcon={<ShoppingCartIcon fontSize="small" />}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToCart(item);
                        }}
                        sx={{
                          px: 2,
                          py: 1,
                          minWidth: 120,
                          backgroundColor: "#ffc20f",
                          color: "#6d1675",
                          borderRadius: 50,
                          textTransform: "none",
                          fontWeight: "bold",
                          fontSize: "0.75rem",
                          "&:hover": { 
                            backgroundColor: "#6d1675",
                            color: "#fff"
                          },
                          boxShadow: "0 2px 8px rgba(255, 194, 15, 0.3)",
                        }}
                      >
                        Add to cart
                      </Button>
                    )}

                    <Button
                      variant="outlined"
                      startIcon={<FlashOnIcon fontSize="small" />}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBuyProduct(item);
                      }}
                      sx={{
                        px: 2,
                        py: 1,
                        minWidth: 100,
                        borderColor: "#6d1675",
                        color: "#6d1675",
                        borderRadius: 50,
                        textTransform: "none",
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                        "&:hover": {
                          borderColor: "#ffc20f",
                          backgroundColor: "#6d1675",
                          color: "#fff",
                        },
                      }}
                    >
                      Buy Now
                    </Button>
                  </Stack>
                </Box>
              </motion.div>
            );
          })}
        </Box>
      </Box>

      {/* Checkout Drawer */}
      {/* <CheckoutDrawer
        cartItems={cartItems}
        savings={savings}
        payAmount={total}
        openCheckout={openCheckout}
        onCloseCheckout={() => setOpenCheckout(false)}
      /> */}
    </>
  );
};

export default RecentlyPurchased;